import * as Rx from 'rxjs'
import { isEqual } from 'lodash'

import Avatar from '../../models/MapObjects/Avatar'
import MapObjectCRS from './MapObjectCRS'
import MapObjectView from './MapObjectView'
import PositionInfo from './PositionInfo'
import PlayerController, {
  PlayerState
} from '../../models/MapObjects/PlayerController'
import {
  MapObjectEvent,
  isAbsentEvent,
  isLoadDataEvent,
  isMoveEvent,
  isPresentEvent
} from '../../io/events'
import { Player } from '../../states/WorldState'
import { Vector } from '../../models/BasicTypes'

export default class PlayerView implements MapObjectView {
  readonly id: string
  readonly removed$ = new Rx.BehaviorSubject(false)
  readonly position$ = new Rx.BehaviorSubject<PositionInfo | undefined>(
    undefined
  )

  private readonly container: HTMLElement
  private lastState?: PlayerState
  private mapObjectCRS: MapObjectCRS
  private controller?: PlayerController
  private subscription?: Rx.Subscription
  private div: HTMLDivElement
  private nameDiv: HTMLDivElement
  private spriteDiv: HTMLDivElement
  private name: string | null = null
  private present = false
  private load = false

  constructor (container: HTMLElement, mapObjectCRS: MapObjectCRS, id: string) {
    this.id = id
    this.container = container
    this.mapObjectCRS = mapObjectCRS
  }

  updateFrame (elasped: number) {
    if (!this.controller) {
      return
    }
    this.controller.updateFrame(elasped)
    this.updateState()
  }

  updateMapObjectCRS (mapObjectCRS: MapObjectCRS) {
    this.mapObjectCRS = mapObjectCRS
    this.updateState()
  }

  processEvent (event: MapObjectEvent) {
    if (this.removed$.value) {
      return
    }
    if (isPresentEvent(event)) {
      this.present = true
      this.controller = new PlayerController({
        position: event.position,
        // TODO: update speed
        speed: 1,
        avatar: Avatar.makeZombie(),
        movingSpeed: (_deltaVector: Vector, _elasped: number) => 1
      })
      this.updateState()
    } else if (isLoadDataEvent(event)) {
      this.load = true
      this.name = (event.data as Player).displayName
      this.updateState()
    } else if (isAbsentEvent(event)) {
      this.removed$.next(true)
      // TODO
      this.unmount()
    } else if (isMoveEvent(event)) {
      this.controller!.move(event)
    }
  }

  private mount () {
    this.div = document.createElement('div')
    this.div.className = 'absolute'
    this.div.style.pointerEvents = 'none'

    this.nameDiv = document.createElement('div')
    this.nameDiv.className = 'absolute center h4 bold'
    this.nameDiv.style.fontFamily = 'Roboto, sans-serif'
    const anyStyle = this.nameDiv.style as any
    // Notice: ideally the stroke should be outside, but it's not supported
    // by css yet. hopefully in the future we can do outside stroking
    // ref: https://github.com/w3c/fxtf-drafts/issues/117
    anyStyle.textStroke = '0.5px white'
    anyStyle.webkitTextStroke = '0.5px white'

    this.spriteDiv = document.createElement('div')
    this.spriteDiv.style.backgroundRepeat = 'no-repeat'

    this.div.appendChild(this.nameDiv)
    this.div.appendChild(this.spriteDiv)

    this.updateState()
    this.container.appendChild(this.div)

    this.updateState()

    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    this.subscription = new Rx.Subscription()
  }

  private unmount () {
    if (!this.div) {
      return
    }
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    this.subscription = undefined
    this.container.removeChild(this.div)
    this.position$.complete()
  }

  private updateState () {
    if (!this.present || !this.load) {
      return
    }
    if (!this.div) {
      this.mount()
    }
    const { mapObjectCRS } = this
    const { state } = this.controller!

    // No need to update when state is the same
    if (state === this.lastState) {
      return
    }

    const { spriteImage, region, anchor, nameOffset, dialogueOffset } = state

    // TODO: no need to set this everytime, also this needs to be set by
    // subscribing name change of player instead
    this.nameDiv.textContent = this.name

    if (!this.lastState || !isEqual(this.lastState.nameOffset, nameOffset)) {
      this.nameDiv.style.left = `${nameOffset.x}px`
      this.nameDiv.style.bottom = `${nameOffset.y}px`
    }

    if (!this.lastState || this.lastState.spriteImage !== spriteImage) {
      this.spriteDiv.style.backgroundImage = `url(${spriteImage})`
    }

    if (!this.lastState || !isEqual(this.lastState.region, region)) {
      this.nameDiv.style.width = `${region.size.width}px`
      this.spriteDiv.style.width = `${region.size.width}px`
      this.spriteDiv.style.height = `${region.size.height}px`
      this.spriteDiv.style.backgroundPosition = `-${region.origin.x}px -${region
        .origin.y}px`
    }

    if (
      !this.lastState ||
      this.lastState.position !== state.position ||
      this.lastState.anchor !== state.anchor ||
      this.lastState.nameOffset !== state.nameOffset ||
      this.lastState.dialogueOffset !== state.dialogueOffset
    ) {
      const position = mapObjectCRS.wanderToRelative(state.position)
      const zIndex = mapObjectCRS.wanderToZIndex(state.position)
      const relPosition = {
        x: position.x - anchor.x,
        y: position.y - anchor.y
      }
      this.div.style.transform = `translate3d(${relPosition.x}px, ${relPosition.y}px, 0px)`
      this.div.style.zIndex = `${zIndex}`

      this.position$.next({
        base: position,
        nameOffset,
        dialogueOffset
      })
    }

    this.lastState = state
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/MapObjects/PlayerView.tsx